import { AlertCircle, KeyRound, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/Button';
import { Wordmark } from '../components/Wordmark';
import { PasswordReveal } from '../components/inputs/PasswordReveal';
import { TextField } from '../components/inputs/TextField';
import { authApi } from '../features/auth/authApi';
import { ApiError } from '../lib/apiError';

/** Mirrors the server's PasswordRules. Kept in step deliberately — see AuthValidators.cs. */
const MINIMUM_LENGTH = 12;

function meetsComplexity(value: string): boolean {
  return (
    /[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value)
  );
}

/**
 * Forced on first sign-in after an administrator issues or resets a password.
 * ProtectedRoute routes here and refuses to leave until `mustChangePassword` clears.
 */
export function ChangePassword() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  /*
    The forced path — the account was issued a password and cannot go anywhere else until it
    is changed. It does not ask for the current password, so nothing here may require one.
  */
  const forcedChange = user?.mustChangePassword ?? false;

  const tooShort = newPassword.length > 0 && newPassword.length < MINIMUM_LENGTH;
  const tooSimple = newPassword.length >= MINIMUM_LENGTH && !meetsComplexity(newPassword);
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  /*
    Only checkable where the current password is on the form. On the forced path the client
    has nothing to compare against — the server still refuses a password identical to the one
    on the account, and that rejection arrives as a field error like any other.
  */
  const sameAsCurrent =
    !forcedChange && newPassword.length > 0 && newPassword === currentPassword;

  const valid =
    (forcedChange || currentPassword.length > 0) &&
    newPassword.length >= MINIMUM_LENGTH &&
    meetsComplexity(newPassword) &&
    newPassword === confirmPassword &&
    !sameAsCurrent;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
    setError(null);

    if (!valid) {
      return;
    }

    setSaving(true);

    try {
      // Omitted entirely on the forced path rather than sent empty — the request either carries
      // a current password or does not claim to.
      await authApi.changePassword(
        forcedChange ? { newPassword } : { currentPassword, newPassword },
      );

      // The server revokes every session on a password change, including this one, so the
      // only correct next step is a fresh sign-in.
      await logout();
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not reach the server. Try again.', 0),
      );

      // A 401 here means our own session died mid-change; re-read it so the UI is honest.
      if (caught instanceof ApiError && caught.status === 401) {
        await refreshUser();
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-panel bg-brand-500 shadow-raised">
            <ShieldCheck className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <div className="text-center">
            <h1 className="text-lg font-semibold text-slate-800"><Wordmark /></h1>
            <p className="text-sm text-slate-500">Integrated systems</p>
          </div>
        </div>

        <div className="rounded-panel border border-slate-200 bg-white p-6 shadow-panel">
          <h2 className="text-sm font-semibold text-slate-800">Choose a new password</h2>
          <p className="mt-1 text-xs text-slate-500">
            {user?.mustChangePassword
              ? 'Your password was issued by an administrator and must be changed before you continue.'
              : 'Update the password for your account.'}
          </p>

          {error ? (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-xs text-red-700">{error.message}</p>
                {Object.values(error.fields)
                  .flat()
                  .map((message) => (
                    <p key={message} className="mt-1 text-xs text-red-600">
                      {message}
                    </p>
                  ))}
              </div>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} noValidate className="mt-5 flex flex-col gap-4">
            {/*
              The current password is asked for only on a VOLUNTARY change.

              On the forced first sign-in it is the password the administrator issued, typed
              minutes ago on the login form to reach this page — asking for it again proves
              nothing the session has not already proved, and it is the field most likely to
              be got wrong, because the reader is holding a value they were handed rather than
              one they chose. The server applies the same rule: it requires the current
              password unless the account is in must-change state.
            */}
            {forcedChange ? null : (
              <TextField
                id="change-current-password"
                label="Current password"
                type="password"
                autoComplete="current-password"
                autoFocus
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={submitted && !currentPassword ? 'Enter your current password.' : undefined}
                leading={<KeyRound className="h-4 w-4" aria-hidden="true" />}
                disabled={saving}
              />
            )}

            <TextField
              id="change-new-password"
              label="New password"
              type={visible ? 'text' : 'password'}
              autoComplete="new-password"
              autoFocus={forcedChange}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              trailing={
                <PasswordReveal
                  id="change-new-password-reveal"
                  visible={visible}
                  onToggle={() => setVisible((v) => !v)}
                />
              }
              description={`At least ${MINIMUM_LENGTH} characters, with an uppercase letter, a lowercase letter, a digit, and a symbol.`}
              error={
                tooShort
                  ? `Use at least ${MINIMUM_LENGTH} characters.`
                  : tooSimple
                    ? 'Add an uppercase letter, a lowercase letter, a digit, and a symbol.'
                    : sameAsCurrent
                      ? 'The new password must differ from your current one.'
                      : undefined
              }
              leading={<KeyRound className="h-4 w-4" aria-hidden="true" />}
              disabled={saving}
            />

            {/*
              One reveal state for both boxes. Revealing only one would leave the reader
              comparing a visible value against a row of dots, which is the comparison the
              control exists to remove.
            */}
            <TextField
              id="change-confirm-password"
              label="Confirm new password"
              type={visible ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={mismatch ? 'The two passwords do not match.' : undefined}
              leading={<KeyRound className="h-4 w-4" aria-hidden="true" />}
              trailing={
                <PasswordReveal
                  id="change-confirm-password-reveal"
                  visible={visible}
                  onToggle={() => setVisible((v) => !v)}
                />
              }
              disabled={saving}
            />

            <Button
              id="change-password-submit"
              type="submit"
              variant="primary"
              fullWidth
              disabled={!valid}
              loading={saving}
              loadingLabel="Updating"
              className="mt-1"
            >
              Update password
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          You will be signed out and asked to sign in with the new password.
        </p>
      </div>
    </main>
  );
}
